import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const first = payload.find(p => p.dataKey === "First 7 Days")?.value || 0;
    const last = payload.find(p => p.dataKey === "Last 7 Days")?.value || 0;
    const growth = first > 0 ? ((last - first) / first * 100).toFixed(1) : "∞";
    const isPositive = last >= first;
    
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-lg">
        <p className="font-heading text-sm mb-3 text-slate-800 font-semibold">{label}</p>
        <div className="space-y-2">
          <p className="text-sm text-slate-600">
            First 7 Days: <span className="font-semibold text-slate-800">{first}</span>
          </p>
          <p className="text-sm text-blue-600">
            Last 7 Days: <span className="font-semibold">{last}</span>
          </p>
          <div className={`flex items-center gap-1 pt-2 border-t border-slate-200 ${
            isPositive ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span className="text-sm font-bold">
              {isPositive ? '+' : ''}{growth}%
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const ConversionGrowthChart = ({ 
  firstValue, 
  lastValue, 
  title = "Conversions"
}) => {
  const data = [
    { name: "First 7 Days", value: firstValue, fill: "#94A3B8" },
    { name: "Last 7 Days", value: lastValue, fill: lastValue > firstValue ? "#059669" : "#DC2626" }
  ];

  const growthPercent = firstValue > 0 
    ? ((lastValue - firstValue) / firstValue * 100).toFixed(0)
    : "∞";
  const isPositive = lastValue >= firstValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="chart-container p-6"
      data-testid={`conversion-chart-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-800">
            {title}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Weekly Growth
          </p>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
          isPositive ? 'bg-emerald-100' : 'bg-red-100'
        }`}>
          {isPositive ? (
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-600" />
          )}
          <span className={`font-heading text-xl font-bold ${
            isPositive ? 'text-emerald-700' : 'text-red-700'
          }`}>
            {isPositive ? '+' : ''}{growthPercent}%
          </span>
        </div>
      </div>
      
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 20, right: 60, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis 
              type="number"
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
              width={100}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[0, 6, 6, 0]}
              animationDuration={1200}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
              <LabelList 
                dataKey="value" 
                position="right" 
                fill="#334155"
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={600}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Visual comparison bars */}
      <div className="mt-6 space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 w-24">First 7 Days</span>
          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-slate-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(firstValue / Math.max(firstValue, lastValue)) * 100}%` }}
              transition={{ duration: 0.8, delay: 0.3 }}
            />
          </div>
          <span className="font-heading text-sm text-slate-600 w-12 text-right">{firstValue}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 w-24">Last 7 Days</span>
          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
            <motion.div 
              className={`h-full rounded-full ${isPositive ? 'bg-emerald-500' : 'bg-red-500'}`}
              initial={{ width: 0 }}
              animate={{ width: `${(lastValue / Math.max(firstValue, lastValue)) * 100}%` }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
          </div>
          <span className={`font-heading text-sm w-12 text-right ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {lastValue}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ConversionGrowthChart;
